<template>
  <nav
    v-if="subMenuItems"
    id="section-menu"
    class="w-full flex flex-col py-4"
  >
    <div class="flex justify-between items-center w-full" @click="toggleMenu">
      <h6 class="nv-m-0">
        {{ productName }}
      </h6>
      <nv-icon>
        {{ menuOpened ? 'expand-less' : 'expand-more' }}
      </nv-icon>
    </div>

    <!-- Content menu -->
    <div class="content">
      <nv-list>
        <nv-list-item
          v-for="(item, idx) in (subMenuItems)"
          :key="idx"
        >
          <a
            v-if="item.url && item.external"
            :href="item.url"
          >
            {{ item.title }}
          </a>
          <nuxt-link
            v-else-if="item.url && !item.external"
            :to="localePath(item.url)"
            @click.native="toggleMenu"
          >
            {{ item.title }}
          </nuxt-link>
          <span
            v-else
            @click="() => goToAnchor(item.anchor)"
          >
            {{ item.title }}
          </span>
          <div v-if="!item.url && !item.anchor" class="nv-ml-1">
            <nv-icon>
              expand-more
            </nv-icon>
          </div>
        </nv-list-item>
      </nv-list>
    </div>
  </nav>
</template>

<script lang="ts" setup>
import type { IRoutesLinksResponse } from '@/utils/types';

// ---------------------------------------------
// Hooks
// ---------------------------------------------
defineProps({
  subMenuItems: {
    default: undefined, type: Array as PropType<IRoutesLinksResponse[]>, required: false,
  },
  productName: { default: undefined, type: String, required: false },
});

const localePath = useLocalePath();

// ---------------------------------------------
// State
// ---------------------------------------------
const menuOpened = ref(false);
const mobileMenu = useState<HTMLNvTabsElement | undefined>(undefined);

// ---------------------------------------------
// Methods
// ---------------------------------------------

function toggleMenu() {
  if (menuOpened.value) {
    const content = document.querySelector('#section-menu .content') as HTMLDivElement;
    content.classList.remove('opened');
    content.style.minHeight = '0px';
    menuOpened.value = false;
    return;
  }

  const content = document.querySelector('#section-menu .content') as HTMLDivElement;
  content.classList.add('opened');
  content.style.minHeight = `${content.scrollHeight}px`;
  menuOpened.value = true;
}

function goToAnchor(anchor?: string) {
  if (!anchor) return;

  toggleMenu();
  requestAnimationFrame(() => {
    const el = document.querySelector(anchor) as any;
    el?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  });
}
</script>
<style lang="scss" scoped>
@import '@nuiverse/core/src/style/_utils';
@import '@nuiverse/core/src/style/_mixins';

nav {
  @extend .transition-exit;
  border-top: 1px solid var(--nv-border-color);
  cursor: pointer;
  overflow: hidden;
  transition-property: height;
}

.content {
  @extend .transition-exit;
  height: 0;
  min-height: 0;
  overflow: hidden;

  &.opened {
    @extend .transition-entrance;
  }
}
</style>
